import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './ExperiencePopup.css';

const ExperiencePopup = ({ isOpen, onClose, onSave, experience = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    description: '',
    start_date: '',
    end_date: '',
    is_current: false,
    employment_type: 'full-time',
    industry: '',
    skills_used: [],
    company_website: '',
    is_public: true,
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [skillsInput, setSkillsInput] = useState('');

  // Helper function to format date for HTML date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Initialize form data when popup opens or experience changes
  useEffect(() => {
    if (isOpen) {
      if (isEdit && experience) {
        setFormData({
          title: experience.title || '',
          company: experience.company || '',
          location: experience.location || '',
          description: experience.description || '',
          start_date: formatDateForInput(experience.start_date),
          end_date: formatDateForInput(experience.end_date),
          is_current: experience.is_current || false,
          employment_type: experience.employment_type || 'full-time',
          industry: experience.industry || '',
          skills_used: experience.skills_used || [],
          company_website: experience.company_website || '',
          is_public: experience.is_public !== undefined ? experience.is_public : true,
        });
        setSkillsInput((experience.skills_used || []).join(', '));
      } else {
        // Reset form for new experience
        setFormData({
          title: '',
          company: '',
          location: '',
          description: '',
          start_date: '',
          end_date: '',
          is_current: false,
          employment_type: 'full-time',
          industry: '',
          skills_used: [],
          company_website: '',
          is_public: true,
        });
        setSkillsInput('');
      }
      setErrors({});
    }
  }, [isOpen, experience, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSkillsChange = (e) => {
    const value = e.target.value;
    setSkillsInput(value);
    
    // Convert comma-separated string to array
    const skillsArray = value.split(',').map(skill => skill.trim()).filter(skill => skill);
    setFormData(prev => ({
      ...prev,
      skills_used: skillsArray
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const payload = { ...formData };
      
      // If current job, don't send end_date
      if (payload.is_current) {
        payload.end_date = null;
      }

      const response = isEdit 
        ? await ApiService.put(`/experiences/${experience.id}`, payload)
        : await ApiService.post('/experiences', payload);

      if (response.success) {
        onSave(response.data);
        onClose();
      } else {
        setErrors(response.errors || { general: response.message || 'Failed to save experience' });
      }
    } catch (err) {
      setErrors({ general: 'Error saving experience: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const employmentTypes = [
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' },
  ];

  if (!isOpen) return null;

  return (
    <div className="experience-popup-overlay">
      <div className="experience-popup">
        <div className="experience-popup-header">
          <div className="experience-popup-title">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                <path d="M6 21v-2a4 4 0 0 1 4-4h.5"></path>
              </svg>
            </div>
            <div className="header-content">
              <h3>{isEdit ? 'Edit Experience' : 'Add Your Experience'}</h3>
              <p>{isEdit ? 'Update your experience details' : 'Build your professional profile'}</p>
            </div>
          </div>
          <button className="experience-popup-close" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="experience-popup-content">
          {errors.general && (
            <div className="experience-popup-error">
              <i className="bi bi-exclamation-triangle"></i>
              {errors.general}
            </div>
          )}

          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="section-title">
                <h4>Basic Information</h4>
                <p>Tell us about your role and company</p>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">
                  Job Title *
                  <span className="field-hint">Your official job title or position</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={errors.title ? 'error' : ''}
                  placeholder="e.g., Senior Software Engineer, Marketing Manager"
                  required
                />
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="company">
                  Company *
                  <span className="field-hint">The company or organization name</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={errors.company ? 'error' : ''}
                  placeholder="e.g., Google, Microsoft, Startup Inc."
                  required
                />
                {errors.company && <span className="error-text">{errors.company}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className={errors.location ? 'error' : ''}
                  placeholder="e.g., San Francisco, CA"
                />
                {errors.location && <span className="error-text">{errors.location}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="employment_type">Employment Type *</label>
                <select
                  id="employment_type"
                  name="employment_type"
                  value={formData.employment_type}
                  onChange={handleChange}
                  className={errors.employment_type ? 'error' : ''}
                  required
                >
                  {employmentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors.employment_type && <span className="error-text">{errors.employment_type}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
              </div>
              <div className="section-title">
                <h4>Duration & Timeline</h4>
                <p>When did you work at this position?</p>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">
                  Start Date *
                  <span className="field-hint">When you started this position</span>
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className={errors.start_date ? 'error' : ''}
                  required
                />
                {errors.start_date && <span className="error-text">{errors.start_date}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="end_date">
                  End Date
                  <span className="field-hint">When you left (leave empty if current)</span>
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className={errors.end_date ? 'error' : ''}
                  disabled={formData.is_current}
                />
                {errors.end_date && <span className="error-text">{errors.end_date}</span>}
              </div>
            </div>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_current"
                  checked={formData.is_current}
                  onChange={handleChange}
                />
                <span className="checkbox-custom"></span>
                <div className="checkbox-content">
                  <span className="checkbox-text">I currently work here</span>
                  <span className="checkbox-hint">Check this if you're still employed at this position</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
              <div className="section-title">
                <h4>Additional Details</h4>
                <p>Share more about your role and achievements</p>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="description">
                Job Description
                <span className="field-hint">Describe your key responsibilities, achievements, and impact</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={errors.description ? 'error' : ''}
                placeholder="• Led a team of 5 developers to deliver a new product feature&#10;• Improved system performance by 40% through optimization&#10;• Collaborated with cross-functional teams to launch successful campaigns&#10;• Managed $2M budget and achieved 25% cost reduction"
                rows="5"
              />
              <div className="textarea-hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Use bullet points to highlight your key achievements and responsibilities</span>
              </div>
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="industry">
                  Industry
                  <span className="field-hint">The industry or sector you worked in</span>
                </label>
                <input
                  type="text"
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={errors.industry ? 'error' : ''}
                  placeholder="e.g., Technology, Healthcare, Finance, E-commerce"
                />
                {errors.industry && <span className="error-text">{errors.industry}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="skills_used">
                  Skills & Technologies
                  <span className="field-hint">Technologies, tools, and skills you used</span>
                </label>
                <input
                  type="text"
                  id="skills_used"
                  value={skillsInput}
                  onChange={handleSkillsChange}
                  className={errors.skills_used ? 'error' : ''}
                  placeholder="e.g., React, Node.js, Python, AWS, Docker, Agile"
                />
                <div className="skills-preview">
                  {formData.skills_used.length > 0 && (
                    <div className="skills-tags-preview">
                      {formData.skills_used.map((skill, index) => (
                        <span key={index} className="skill-tag-preview">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {errors.skills_used && <span className="error-text">{errors.skills_used}</span>}
              </div>
            </div>

          </div>

          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div className="section-title">
                <h4>Company Details</h4>
                <p>Add company website (optional)</p>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="company_website">
                Company Website
                <span className="field-hint">The company's official website</span>
              </label>
              <input
                type="url"
                id="company_website"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                className={errors.company_website ? 'error' : ''}
                placeholder="https://company.com"
              />
              {errors.company_website && <span className="error-text">{errors.company_website}</span>}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <div className="section-title">
                <h4>Privacy Settings</h4>
                <p>Control who can see this experience</p>
              </div>
            </div>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                />
                <span className="checkbox-custom"></span>
                <div className="checkbox-content">
                  <span className="checkbox-text">Make this experience public</span>
                  <span className="checkbox-hint">This experience will be visible to other users on your profile</span>
                </div>
              </label>
            </div>
          </div>

          <div className="experience-popup-footer">
            <div className="experience-popup-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-save" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    {isEdit ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg"></i>
                    {isEdit ? 'Update Experience' : 'Save Experience'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExperiencePopup;
